import { useCallback, useEffect, useRef, useState } from "react";
import { useAccess } from "../../../../access/hooks/useAccess";
import { ludDashboardPermissions } from "../../../../access/modules/lud-dashboard";
import { Button, EmptyState, Input, Tags } from "../../../ui";
import { confirmBranchApplicationAPI, finalizeBranchRequestAPI, getBranchWorkflowAPI } from "../../../../utils/api/branchRequests";
import { getRequestEmailsAPI, retryRequestEmailAPI } from "../../../../utils/api/requestEmails";
import ActionDialog from "./ActionDialog";
import { BRANCH_EMAIL_PURPOSE, localized } from "./BranchActionDialog";
import { actorText, formatDateTime } from "./requestHelpers";
import { pick } from "./unifiedText";
import "./BranchWorkflow.css";

// The inline panel of a cashier branch-change request (working hours / maintenance
// closure), opened from the request's row. The row itself carries Approve / Reject,
// Send and Finalize; this panel carries what is per application:
//   the status of each targeted application, Confirm, Retry (incl. the acknowledged
//   retry of an uncertain e-mail), the administrator override, and - for a working
//   hours request - the before / after hours.
// Everything shown or offered comes from the server (`workflow.can` is a hint, the
// endpoints decide). Cashiers see the same panel as read-only progress. Sending an
// e-mail is never a confirmation, and nothing here changes the restaurant's data
// except "Finalize", which the server performs once.

const DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const STATE_VARIANT = { pending: "neutral", missingRecipient: "danger", sending: "info", sent: "info", failed: "danger", uncertain: "warning", confirmed: "success" };

const shiftText = (range) => (range && range.from && range.to ? `${range.from} - ${range.to}` : "");

function hoursOf(workTime, day) {
  const days = Array.isArray(workTime) ? workTime[0]?.days : null;
  return days ? [shiftText(days[day]?.morning), shiftText(days[day]?.evening)].filter(Boolean) : null;
}

function HoursTable({ wf, t }) {
  const b = t.branch;
  const hasOriginal = Boolean(wf.original?.workTime?.length);
  return (
    <div className="lud-ur-table-wrap">
      <table className="lud-ur-changes lud-bw-hours">
        <caption className="lud-bw-sr">{b.hoursTitle}</caption>
        <thead>
          <tr>
            <th scope="col">{b.day}</th>
            <th scope="col">{b.current}</th>
            <th scope="col">{b.requested}</th>
          </tr>
        </thead>
        <tbody>
          {DAYS.map((day) => {
            const before = hasOriginal ? hoursOf(wf.original.workTime, day) : null;
            const after = hoursOf(wf.requested.workTime, day);
            if (after === null && before === null) return null;
            const changed = hasOriginal && JSON.stringify(before) !== JSON.stringify(after);
            return (
              <tr key={day} className={changed ? "is-changed" : undefined}>
                <th scope="row">{b.days[day]}</th>
                <td dir="ltr">{before === null ? b.notRecorded : before.length ? before.join(b.shiftSep) : b.closed}</td>
                <td dir="ltr">{after === null ? b.notRecorded : after.length ? after.join(b.shiftSep) : b.closed}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function BranchWorkflow({ request, t, lang, onChanged }) {
  const token = localStorage.getItem("auth-token");
  const { has } = useAccess();
  const b = t.branch;
  const id = request.identity.id;
  const [state, setState] = useState({ phase: "loading", wf: null, error: null });
  const [dialog, setDialog] = useState(null);
  const [mail, setMail] = useState({ emails: [] });
  const [refText, setRefText] = useState("");
  const [busyEmail, setBusyEmail] = useState(null);
  const [emailError, setEmailError] = useState("");
  const seq = useRef(0);

  const load = useCallback(() => {
    const controller = new AbortController();
    const mine = ++seq.current;
    getBranchWorkflowAPI(token, id, { signal: controller.signal })
      .then((wf) => mine === seq.current && setState({ phase: "ready", wf, error: null }))
      .catch((error) => {
        if (error?.name === "AbortError" || mine !== seq.current) return;
        setState({ phase: "error", wf: null, error });
      });
    return () => controller.abort();
  }, [token, id]);
  useEffect(() => load(), [load]);

  const wf = state.wf;
  const reviewer = Boolean(wf && (wf.can.review || wf.can.send || wf.can.confirm || wf.can.finalize || wf.applications.some((a) => a.recipients)));

  const loadEmails = useCallback(() => {
    if (!reviewer) return;
    getRequestEmailsAPI(token, "dashboard", id)
      .then((data) => setMail({ emails: (data?.emails || []).filter((email) => email.purpose === BRANCH_EMAIL_PURPOSE.code) }))
      .catch(() => setMail({ emails: [] }));
  }, [reviewer, token, id]);
  useEffect(() => loadEmails(), [loadEmails, wf?.applications?.length]);

  const refresh = async () => {
    load();
    loadEmails();
    await onChanged?.();
  };

  if (state.phase === "loading") return <EmptyState variant="loading" compact>{t.loading}</EmptyState>;
  if (state.phase === "error") {
    // not visible to this viewer / not a workflow request: nothing to show
    if (state.error?.status === 404 || state.error?.status === 403) return null;
    return (
      <EmptyState variant="error" compact title={b.loadError} action={<Button size="sm" onClick={load}>{t.retry}</Button>}>
        {state.error?.message}
      </EmptyState>
    );
  }

  const appLabel = (app) => pick(app.label, lang) || app.application;
  const adminRole = has(ludDashboardPermissions.applications.review); // the workflow administrators: ceo-level, am, dm, dmm
  const run = async (fn) => {
    try {
      const next = await fn();
      setState({ phase: "ready", wf: next, error: null });
      await refresh();
    } catch (error) {
      throw localized(error, t);
    }
  };

  const outstanding = wf.applications.filter((app) => app.state !== "confirmed");
  const emailByApp = new Map(mail.emails.map((email) => [email.application, email]));

  const retryEmail = async (email, acknowledge) => {
    setBusyEmail(email.id);
    setEmailError("");
    try {
      await retryRequestEmailAPI(token, email.id, acknowledge ? { acknowledgeUncertain: true } : {});
      await refresh();
    } catch (error) {
      setEmailError(localized(error, t).message);
      throw localized(error, t);
    } finally {
      setBusyEmail(null);
    }
  };

  const openRetry = (email) => {
    if (email.status === "uncertain") setDialog({ type: "uncertain", email });
    else retryEmail(email, false).catch(() => {});
  };

  const lastAttempt = (email) => email?.attempts?.[email.attempts.length - 1];

  return (
    <section className="lud-bw" aria-label={b.applications}>
      {wf.action === "updateBranchHours" && <HoursTable wf={wf} t={t} />}
      {wf.applications.length > 0 && <p className="lud-ur-muted">{b.summaryOf(wf.summary.confirmed, wf.summary.total)}</p>}
      {!wf.applications.length && wf.status !== "pending" && <p className="lud-ur-muted">{b.noTargets}</p>}
      {wf.applications.length > 0 && (
        <ul className="lud-bw-apps">
          {wf.applications.map((app) => {
            const email = emailByApp.get(app.application);
            const last = lastAttempt(email);
            const canRetry = email && (email.status === "failed" || (email.status === "uncertain" && adminRole));
            return (
              <li key={app.application} className={`lud-bw-app is-${app.state}`}>
                <div className="lud-bw-app-head">
                  <strong>{appLabel(app)}</strong>
                  <Tags size="sm" variant={STATE_VARIANT[app.state] || "neutral"} appearance="soft">{b.state[app.state] || app.state}</Tags>
                </div>
                {b.stateNote[app.state] && <p className="lud-ur-muted">{b.stateNote[app.state]}</p>}
                <dl className="lud-bw-meta">
                  {app.sentAt && <><dt>{b.colSent}</dt><dd>{formatDateTime(app.sentAt, lang)}</dd></>}
                  {app.lastAttemptAt && <><dt>{b.colAttempt}</dt><dd>{formatDateTime(app.lastAttemptAt, lang)}{app.attempts ? ` · ${b.attemptsCount(app.attempts)}` : ""}</dd></>}
                  {app.recipients && <><dt>{b.colRecipients}</dt><dd dir="ltr">{[...app.recipients.to, ...app.recipients.cc].join(", ")}</dd></>}
                  <dt>{b.colConfirmation}</dt>
                  <dd>
                    {app.confirmation
                      ? [b.confirmedBy(actorText(app.confirmation.by)), formatDateTime(app.confirmation.at, lang), app.confirmation.reference, app.confirmation.note].filter(Boolean).join(" · ")
                      : b.notConfirmed}
                  </dd>
                </dl>
                <div className="lud-ur-action-row">
                  {wf.can.confirm && app.state === "sent" && (
                    <Button size="sm" variant="success" appearance="soft" onClick={() => { setRefText(""); setDialog({ type: "confirm", app }); }}>{b.confirm}</Button>
                  )}
                  {wf.can.send && canRetry && (
                    <Button size="sm" variant="primary" appearance="soft" disabled={Boolean(busyEmail)} loading={busyEmail === email.id} onClick={() => openRetry(email)}>
                      {email.status === "uncertain" ? b.retryUncertain : b.retrySend}
                    </Button>
                  )}
                  {wf.can.send && email?.status === "uncertain" && !adminRole && <span className="lud-ur-muted">{b.uncertainAdminOnly}</span>}
                </div>
                {last?.errorKind && email.status !== "sent" && <p className="lud-ur-muted" dir="auto">{last.error}</p>}
              </li>
            );
          })}
        </ul>
      )}
      {emailError && <p className="lud-ur-error" role="alert">{emailError}</p>}
      {wf.can.override && (
        <div className="lud-ur-action-row">
          <Button size="sm" variant="danger" appearance="soft" onClick={() => setDialog({ type: "override" })}>{b.override}</Button>
        </div>
      )}

      {wf.applyError && <p className="lud-ur-error" role="alert">{b.applyError}: {wf.applyError}</p>}
      {wf.finalize?.override && (
        <div className="lud-bw-override" role="note">
          <strong>{b.completedWithOverride}</strong>
          <p>{b.overrideInfo}</p>
          <ul className="lud-ur-plain">{wf.finalize.outstanding.map((app) => <li key={app.application}>{appLabel(app)} — {b.state[app.state] || app.state}</li>)}</ul>
          <p className="lud-ur-muted">{b.overrideBy}: {[actorText(wf.finalize.by), formatDateTime(wf.finalize.at, lang)].filter(Boolean).join(" · ")}</p>
          {wf.finalize.reason && <p className="lud-ur-note"><strong>{b.overrideReasonShown}:</strong> {wf.finalize.reason}</p>}
        </div>
      )}

      {dialog?.type === "confirm" && (
        <ActionDialog t={t} title={b.confirmTitle(appLabel(dialog.app))} description={b.confirmText} reason={{ label: b.confirmNote, required: false }} ack={b.confirmAck} confirmLabel={b.confirm} onClose={() => setDialog(null)} onConfirm={async (note) => { await run(() => confirmBranchApplicationAPI(token, id, dialog.app.application, { note, reference: refText.trim() })); setDialog(null); }}>
          <Input label={b.confirmReference} value={refText} onChange={(event) => setRefText(event.target.value)} />
        </ActionDialog>
      )}
      {dialog?.type === "override" && (
        <ActionDialog t={t} title={b.overrideTitle} description={b.overrideWarn} reason={{ label: b.overrideReason, required: true, min: 5 }} ack={b.overrideAck} confirmLabel={b.override} danger onClose={() => setDialog(null)} onConfirm={async (reason) => { await run(() => finalizeBranchRequestAPI(token, id, { override: true, reason })); setDialog(null); }}>
          <p><strong>{b.outstanding}</strong></p>
          <ul className="lud-ur-plain">{(outstanding.length ? outstanding : wf.impact || []).map((app) => <li key={app.application}>{appLabel(app)} — {b.state[app.state || (app.recipientMissing ? "missingRecipient" : "pending")]}</li>)}</ul>
        </ActionDialog>
      )}
      {dialog?.type === "uncertain" && (
        <ActionDialog t={t} title={b.uncertainTitle(appLabel(wf.applications.find((a) => a.application === dialog.email.application) || { application: dialog.email.application }))} description={b.uncertainWarn} ack={b.uncertainAck} confirmLabel={b.retryUncertain} danger onClose={() => setDialog(null)} onConfirm={async () => { await retryEmail(dialog.email, true); setDialog(null); }}>
          <dl className="lud-bw-meta">
            <dt>{b.uncertainLast}</dt>
            <dd>{[t.email.result[lastAttempt(dialog.email)?.result] || lastAttempt(dialog.email)?.result, formatDateTime(lastAttempt(dialog.email)?.finishedAt || lastAttempt(dialog.email)?.startedAt, lang), t.email.errorKind[lastAttempt(dialog.email)?.errorKind], lastAttempt(dialog.email)?.providerMessageId].filter(Boolean).join(" · ")}</dd>
            <dt>{b.colRecipients}</dt>
            <dd dir="ltr">{[...dialog.email.message.to, ...(dialog.email.message.cc || [])].join(", ")}</dd>
          </dl>
          <p>{t.email.uncertainNote}</p>
        </ActionDialog>
      )}
    </section>
  );
}
